export interface Tour {
  id?: string;
  name: string;
  ruleset_id: string;
  prize: string;
  description: string;
  date: string;
  type: 'recurring_weekly' | 'one_time';
  published: boolean;
  discord_id: string;
  timezone: string;
  realm: 'europe' | 'americas' | 'asia';
  max_participants: number;
  created_at?: string;
  updated_at?: string;
  team_name?: string;
  ruleset_name?: string;
}

export interface Ruleset {
  id: string;
  name: string;
  description?: string;
}

export interface Discord {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  logo_url?: string;
}

export interface TourParticipant {
  tour_id: string;
  user_id: string;
  joined_at?: string;
}

export interface TourParticipantStatus {
  is_participant: boolean;
  participant_count: number;
}

export interface TourFormData
  extends Omit<Tour, 'id' | 'created_at' | 'updated_at' | 'team_name' | 'ruleset_name'> {
  id?: string;
}

export interface TourFilters {
  published?: boolean;
  type?: 'recurring_weekly' | 'one_time';
  realm?: 'europe' | 'americas' | 'asia';
  discord_id?: string;
  ruleset_id?: string;
}

export interface TourState {
  tours: Tour[];
  discords: Discord[];
  rulesets: Ruleset[];
  participants: Record<string, TourParticipant[]>; // tourId -> participants
  participantStatus: Record<string, TourParticipantStatus>; // tourId -> status
  loading: {
    tours: boolean;
    discords: boolean;
    rulesets: boolean;
    creating: boolean;
    updating: boolean;
    deleting: boolean;
    participants: boolean;
    joining: boolean;
    leaving: boolean;
  };
  error: {
    tours: string | null;
    discords: string | null;
    rulesets: string | null;
    creating: string | null;
    updating: string | null;
    deleting: string | null;
    participants: string | null;
    joining: string | null;
    leaving: string | null;
  };
  filters: TourFilters;
  editingTour: Tour | null;
}

// Utility functions
export const createDefaultTour = (): TourFormData => ({
  name: '',
  ruleset_id: '',
  prize: '',
  description: '',
  date: new Date().toISOString().slice(0, 16),
  type: 'one_time',
  published: false,
  discord_id: '',
  timezone: 'UTC',
  realm: 'europe',
  max_participants: 8,
});

export const formatTourDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return dateString;
  }
};

export const formatDateForInput = (date: Date): string => {
  return date.toISOString().slice(0, 16);
};

export const formatDateForAPI = (dateString: string): string => {
  // If the dateString is already in full ISO format, return it
  if (dateString.includes('Z') || dateString.includes('+')) {
    return dateString;
  }

  // Otherwise, parse the datetime-local value and add seconds and timezone
  const date = new Date(dateString);
  return date.toISOString(); // Returns format: YYYY-MM-DDThh:mm:ss.sssZ
};

export const getDiscordTeamName = (discordId: string, discords: Discord[] | null): string => {
  if (!discords || !Array.isArray(discords)) {
    return 'No Team';
  }
  const discord = discords.find((d) => d.id === discordId);
  return discord ? discord.name : 'No Team';
};

export const getRulesetName = (rulesetId: string, rulesets: Ruleset[] | null): string => {
  if (!rulesets || !Array.isArray(rulesets)) {
    return 'No Ruleset';
  }
  const ruleset = rulesets.find((r) => r.id === rulesetId);
  return ruleset ? ruleset.name : 'No Ruleset';
};

export const filterTours = (tours: Tour[] | null, filters: TourFilters): Tour[] => {
  if (!tours || !Array.isArray(tours)) {
    return [];
  }

  return tours.filter((tour) => {
    if (filters.published !== undefined && tour.published !== filters.published) {
      return false;
    }
    if (filters.type && tour.type !== filters.type) {
      return false;
    }
    if (filters.realm && tour.realm !== filters.realm) {
      return false;
    }
    if (filters.discord_id && tour.discord_id !== filters.discord_id) {
      return false;
    }
    if (filters.ruleset_id && tour.ruleset_id !== filters.ruleset_id) {
      return false;
    }
    return true;
  });
};
